import { useEffect, useRef, useState } from "react";
import { animate, useInView } from "framer-motion";
import { formatNumber } from "../lib/utils";

type Props = {
  value: number;
  format?: (n: number) => string;
  decimals?: number;
  suffix?: string;
  className?: string;
  duration?: number;
};

export function AnimatedNumber({
  value,
  format,
  decimals = 0,
  suffix = "",
  className,
  duration = 1.1,
}: Props) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [display, setDisplay] = useState(0);
  // Counts from the number on screen, so a live refresh rolls 63 -> 67
  // instead of restarting from 0 (2026-10-04).
  const shown = useRef(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(shown.current, value, {
      duration,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => {
        shown.current = v;
        setDisplay(v);
      },
    });
    return () => controls.stop();
  }, [inView, value, duration]);

  const text = format
    ? format(display)
    : decimals > 0
      ? display.toFixed(decimals)
      : formatNumber(Math.round(display));

  return (
    <span ref={ref} className={className}>
      {text}
      {suffix}
    </span>
  );
}
